import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useCms } from "../CmsContext";
import { EditorLayout } from "../EditorChrome";
import { StructuredFields, TextField, TextAreaField, ToggleField, ImageField, SelectField } from "../Fields";
import { deepClone, deepEqual, documentSitePath, formatTime } from "../cmsUtils";

function SeoEditor({ seo, onChange, siteUrl }) {
  const s = seo || {};
  const set = (key, value) => onChange({ ...s, [key]: value });

  const ogImageSrc = s.ogImage
    ? s.ogImage.startsWith("/")
      ? `${siteUrl.replace(/\/$/, "")}${s.ogImage}`
      : s.ogImage
    : "";

  return (
    <div className="lw-seo">
      <div className="lw-seo__fields">
        <TextField
          label="Meta title"
          value={s.title}
          onChange={(v) => set("title", v)}
          hint={
            s.absoluteTitle
              ? "Shown exactly as written."
              : "Shown as “Title | Landmark” in search results and browser tabs."
          }
        />
        <ToggleField
          label="Use title exactly (skip the “| Landmark” suffix)"
          value={!!s.absoluteTitle}
          onChange={(v) => set("absoluteTitle", v)}
        />
        <TextAreaField
          label="Meta description"
          value={s.description}
          rows={3}
          onChange={(v) => set("description", v)}
          hint={`${(s.description || "").length}/160 characters — aim for 150–160.`}
        />
        <TextField
          label="Canonical path"
          value={s.canonical}
          onChange={(v) => set("canonical", v)}
          hint="Relative path, e.g. /about"
        />
        <ImageField
          label="Social share image (Open Graph / Twitter)"
          value={s.ogImage}
          onChange={(v) => set("ogImage", v || undefined)}
          hint="Recommended 1200×630. Falls back to the site default when empty."
        />
        <SelectField
          label="Twitter card style"
          value={s.twitterCard || ""}
          onChange={(v) => set("twitterCard", v || undefined)}
          options={[
            { value: "", label: "Site default (large image)" },
            { value: "summary_large_image", label: "Large image" },
            { value: "summary", label: "Small summary" },
          ]}
        />
        <ToggleField
          label="Hide this page from search engines (noindex)"
          value={!!s.noIndex}
          onChange={(v) => set("noIndex", v)}
        />
      </div>

      <div className="lw-seo__preview">
        <p className="lw-field__label">Share preview</p>
        <div className="lw-socialcard">
          <div className="lw-socialcard__image">
            {ogImageSrc ? <img src={ogImageSrc} alt="" /> : <span>Site default image</span>}
          </div>
          <div className="lw-socialcard__body">
            <p className="lw-socialcard__domain">landmarkventures.com</p>
            <p className="lw-socialcard__title">
              {s.title ? (s.absoluteTitle ? s.title : `${s.title} | Landmark`) : "Page title"}
            </p>
            <p className="lw-socialcard__desc">{s.description || "Meta description appears here."}</p>
          </div>
        </div>
        <p className="lw-field__hint">Approximation of how links render on X, LinkedIn and Slack.</p>
      </div>
    </div>
  );
}

export function PageEditorPage() {
  const { docKey } = useParams();
  const key = decodeURIComponent(docKey);
  const { api, API_BASE, refreshPublishStatus, bumpPreview, config } = useCms();

  const [documentMeta, setDocumentMeta] = useState(null);
  const [draft, setDraft] = useState(null); // { content, seo }
  const [savedDraft, setSavedDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("content");
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    (async () => {
      const res = await api.get(`${API_BASE}/documents/${encodeURIComponent(key)}`);
      if (res && res.success) {
        const d = deepClone(res.document.draft || { content: {}, seo: {} });
        setDocumentMeta(res.document);
        setDraft(d);
        setSavedDraft(deepClone(d));
      } else {
        setLoadError(res?.error || "Failed to load this page");
      }
    })();
  }, [api, API_BASE, key]);

  const dirty = draft != null && !deepEqual(draft, savedDraft);

  const save = useCallback(async () => {
    if (!draft) return;
    setSaving(true);
    const res = await api.post(`${API_BASE}/documents/save`, { key, draft });
    setSaving(false);
    if (res && res.success) {
      setSavedDraft(deepClone(draft));
      toast.success("Draft saved");
      refreshPublishStatus();
      bumpPreview();
    } else {
      toast.error(res?.error || "Save failed");
    }
  }, [api, API_BASE, key, draft, refreshPublishStatus, bumpPreview]);

  if (loadError) {
    return <p className="lw-empty">{loadError}</p>;
  }
  if (!draft) {
    return <p className="lw-empty">Loading…</p>;
  }

  const sitePath = documentSitePath(key);
  const hasContent = draft.content && Object.keys(draft.content).length > 0;

  return (
    <EditorLayout
      backTo="../pages"
      backLabel="All pages"
      title={documentMeta?.label || key}
      meta={
        documentMeta?.updatedTime
          ? `Last edited ${formatTime(documentMeta.updatedTime)}${
              documentMeta.updatedBy?.fullName ? ` by ${documentMeta.updatedBy.fullName}` : ""
            }`
          : undefined
      }
      dirty={dirty}
      saving={saving}
      onSave={save}
      sitePath={sitePath}
      headerExtra={
        <div className="lw-tabs">
          <button
            type="button"
            className={`lw-tabs__tab ${tab === "content" ? "lw-tabs__tab--active" : ""}`}
            onClick={() => setTab("content")}
          >
            Content
          </button>
          <button
            type="button"
            className={`lw-tabs__tab ${tab === "seo" ? "lw-tabs__tab--active" : ""}`}
            onClick={() => setTab("seo")}
          >
            SEO
          </button>
        </div>
      }
    >
      {tab === "content" ? (
        hasContent ? (
          <StructuredFields
            value={draft.content}
            onChange={(content) => setDraft({ ...draft, content })}
          />
        ) : (
          <p className="lw-empty">
            This page has no editable copy — only SEO settings. Switch to the SEO tab.
          </p>
        )
      ) : (
        <SeoEditor
          seo={draft.seo}
          onChange={(seo) => setDraft({ ...draft, seo })}
          siteUrl={config.siteUrl}
        />
      )}
    </EditorLayout>
  );
}
